export type PinholeIntrinsics = {
    fx: number;
    fy: number;
    cx: number;
    cy: number;
};

/** Full-angle FOV (deg) → focal length in pixels for that image axis span. */
export function focalLengthPxFromFovDeg(fovDeg: number, spanPx: number): number {
    const halfRad = ((fovDeg * Math.PI) / 180) / 2;
    const tanHalf = Math.tan(halfRad);
    if (!Number.isFinite(tanHalf) || tanHalf <= 0) {
        return spanPx / 2;
    }
    return spanPx / 2 / tanHalf;
}

/** Vertical FOV implied by horizontal FOV and square pixels on a given aspect ratio. */
export function verticalFovDegFromHorizontal(hfovDeg: number, widthPx: number, heightPx: number): number {
    if (!widthPx || !heightPx) {
        return hfovDeg;
    }
    const halfHRad = ((hfovDeg * Math.PI) / 180) / 2;
    const halfVRad = Math.atan((heightPx / widthPx) * Math.tan(halfHRad));
    return (halfVRad * 2 * 180) / Math.PI;
}

/**
 * Pinhole intrinsics from independent horizontal and vertical full-angle FOVs.
 * fx uses image width + hfov; fy uses image height + vfov (not fy = fx).
 */
export function computePinholeIntrinsics(
    widthPx: number,
    heightPx: number,
    hfovDeg: number,
    vfovDeg: number,
): PinholeIntrinsics {
    return {
        fx: focalLengthPxFromFovDeg(hfovDeg, widthPx),
        fy: focalLengthPxFromFovDeg(vfovDeg, heightPx),
        cx: widthPx / 2,
        cy: heightPx / 2,
    };
}
